import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Box, Row } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useDraftsAiSearchStore } from '../hooks/useDraftsAiSearchStore';
import { presentCollectionQuantifiers } from './CollectionChips/collection-predicate';
import { CollectionChips } from './CollectionChips/CollectionChips';
import { unknownFilterChipKeys } from './DraftsAiSearchChips.utils';
import { OrGroup } from './OrGroup/OrGroup';
import { buildPredicateChipUnits, buildUnknownChipUnits } from './PredicateChips/PredicateChips.utils';
import { SettingsChipToneProvider } from './settingsChipTone';
export const GetDraftsAiSearchChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const DraftsAiSearchChips = (param)=>{
    let { testId, trailing, isInlineEditMode = false } = param;
    var _ref, _filter_or;
    const { DraftsAiSearchChipsTestIds } = useMemo(()=>GetDraftsAiSearchChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const filter = useDraftsAiSearchStore((s)=>s.filter);
    const { setAgreementName, setClientName, setContactName, setStatus, setEffectiveDate, setEffectiveDateMode, setLastUpdatedAt, setLastModifiedBy, setNetTerms, setPaymentSettings, setAgreementSettings, setServices, setPackages, setBundles, setOrArm, toggleConnector, clearGroup, removeUnknownField } = useDraftsAiSearchStore(useShallow((s)=>({
            setAgreementName: s.setAgreementName,
            setClientName: s.setClientName,
            setContactName: s.setContactName,
            setStatus: s.setStatus,
            setEffectiveDate: s.setEffectiveDate,
            setEffectiveDateMode: s.setEffectiveDateMode,
            setLastUpdatedAt: s.setLastUpdatedAt,
            setLastModifiedBy: s.setLastModifiedBy,
            setNetTerms: s.setNetTerms,
            setPaymentSettings: s.setPaymentSettings,
            setAgreementSettings: s.setAgreementSettings,
            setServices: s.setServices,
            setPackages: s.setPackages,
            setBundles: s.setBundles,
            setOrArm: s.setOrArm,
            toggleConnector: s.toggleConnector,
            clearGroup: s.clearGroup,
            removeUnknownField: s.removeUnknownField
        })));
    if (filter == null) return null;
    const showPackages = filter.packages != null && presentCollectionQuantifiers(filter.packages).length > 0;
    const showBundles = filter.bundles != null && presentCollectionQuantifiers(filter.bundles).length > 0;
    const groupArms = (_ref = (_filter_or = filter.or) !== null && _filter_or !== void 0 ? _filter_or : filter.and) !== null && _ref !== void 0 ? _ref : null;
    const groupTone = filter.or != null ? 'or' : 'and';
    const showGroup = groupArms != null && groupArms.length > 0;
    const predicateTestIds = {
        AgreementName: DraftsAiSearchChipsTestIds.AgreementName(),
        ClientName: DraftsAiSearchChipsTestIds.ClientName(),
        ContactName: DraftsAiSearchChipsTestIds.ContactName(),
        Status: DraftsAiSearchChipsTestIds.Status(),
        EffectiveDate: DraftsAiSearchChipsTestIds.EffectiveDate(),
        EffectiveDateMode: DraftsAiSearchChipsTestIds.EffectiveDateMode(),
        LastUpdated: DraftsAiSearchChipsTestIds.LastUpdated(),
        ModifiedBy: DraftsAiSearchChipsTestIds.ModifiedBy(),
        NetDays: DraftsAiSearchChipsTestIds.NetDays(),
        Settings: DraftsAiSearchChipsTestIds.Settings(),
        Services: DraftsAiSearchChipsTestIds.Services(),
        Packages: DraftsAiSearchChipsTestIds.Packages(),
        Bundles: DraftsAiSearchChipsTestIds.Bundles(),
        Unknown: (key)=>DraftsAiSearchChipsTestIds.Unknown(key)
    };
    const callbacks = {
        onAgreementNameChange: setAgreementName,
        onClientNameChange: setClientName,
        onContactNameChange: setContactName,
        onStatusChange: setStatus,
        onEffectiveDateChange: setEffectiveDate,
        onEffectiveDateModeChange: setEffectiveDateMode,
        onLastUpdatedAtChange: setLastUpdatedAt,
        onLastModifiedByChange: setLastModifiedBy,
        onNetTermsChange: setNetTerms,
        onPaymentSettingsChange: setPaymentSettings,
        onAgreementSettingsChange: setAgreementSettings,
        onServicesChange: setServices
    };
    const units = buildPredicateChipUnits({
        predicate: filter,
        testIds: predicateTestIds,
        callbacks
    });
    if (showPackages && filter.packages != null) {
        units.push({
            key: 'packages',
            node: /*#__PURE__*/ _jsx(CollectionChips, {
                testId: DraftsAiSearchChipsTestIds.Packages(),
                fieldLabel: t.packagesLabel(),
                nounLabel: t.packagesNoun(),
                collection: filter.packages,
                toggleBiName: "drafts_ai_search_chip_toggle_packages_quantifier",
                removeBiName: "drafts_ai_search_chip_remove_packages",
                onChange: setPackages
            })
        });
    }
    if (showBundles && filter.bundles != null) {
        units.push({
            key: 'bundles',
            node: /*#__PURE__*/ _jsx(CollectionChips, {
                testId: DraftsAiSearchChipsTestIds.Bundles(),
                fieldLabel: t.bundlesLabel(),
                nounLabel: t.bundlesNoun(),
                collection: filter.bundles,
                toggleBiName: "drafts_ai_search_chip_toggle_bundles_quantifier",
                removeBiName: "drafts_ai_search_chip_remove_bundles",
                onChange: setBundles
            })
        });
    }
    if (showGroup && groupArms != null) {
        units.push({
            key: 'orGroup',
            node: /*#__PURE__*/ _jsx(OrGroup, {
                testId: DraftsAiSearchChipsTestIds.OrGroup(),
                arms: groupArms,
                tone: groupTone,
                onArmChange: setOrArm,
                onToggleConnector: toggleConnector,
                onClearGroup: clearGroup
            })
        });
    }
    units.push(...buildUnknownChipUnits({
        keys: unknownFilterChipKeys(filter),
        testIds: predicateTestIds,
        onRemove: removeUnknownField
    }));
    return /*#__PURE__*/ _jsx(SettingsChipToneProvider, {
        value: isInlineEditMode ? 'default' : 'settings',
        children: /*#__PURE__*/ _jsx(Box, {
            testId: DraftsAiSearchChipsTestIds.Root(),
            children: /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                gap: 8,
                wrap: true,
                children: [
                    units.map((unit)=>/*#__PURE__*/ _jsx(Fragment, {
                            children: unit.node
                        }, unit.key)),
                    trailing
                ]
            })
        })
    });
};
